import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { State } from './state';
import {map, Observable, of} from 'rxjs';
import { Appointment } from '../model/calendar';
import {FamilyEvaluationAppointment, FamilyInterventionAppointment} from '../model/familyAndTeam';
import dayjs from 'dayjs';


@Injectable({
  providedIn: 'root'
})
export class AppointmentService {

  loadCount: number = 0;


  constructor(private http: HttpClient) {
  }
  // https://api-qa.huddy.io/api/schedulable-items?teamId=-1&size=10&search=&sortDir=desc&sortField=is_late_flag
  getSchedulableItems(teamId: number, size: number, offset: number, search: string, sortDir: string, sortField: string) {
    console.log('entering get schedulable items');
    const headers = State.getAuthHeaders();
    const params = new HttpParams()
      .append('teamId', '-1')
      .append('offset', offset || 0)
      .append('size', size || 10)
      .append('search', search || '')
      .append('sortDir', sortDir || 'desc')
      .append('sortBy', sortField || 'is_late_flag');
    return this.http.get(State.config.host + '/api/schedulable-items', { headers: headers, params: params })
      .pipe(map((response: any) => response,
        (error: any) => {
          throw error;
        }));
  }


  getCalendarItems() {
    console.log('entering get calendar items');
    const headers = State.getAuthHeaders();
    if (State.user) {
      const params = new HttpParams()
        .append('email', '' + State.user?.trustedUser)
        .append('appointmentsOnly', '1');
      return this.http.get(State.config.host + '/api/calendar-items', { headers: headers, params: params })
        .pipe(map((response: any) => response,
          (error: any) => {
            throw error;
          }));
    }
    return of(undefined);
  }

  getFamilyAppointments(familyId: number) {
    console.log('entering getFamilyAppointments');
    const headers = State.getAuthHeaders();
    if (State.user) {
      const params = new HttpParams()
        .append('familyId', '' + familyId);
      return this.http.get(State.config.host + '/api/appointments', { headers: headers, params: params })
        .pipe(map((response: any) => response,
          (error: any) => {
            throw error;
          }));
    }
    return of(undefined);
  }

  getAppointmentById(id: number) {
    console.log('entering getAppointmentById');
    const headers = State.getAuthHeaders();

    return this.http.get(State.config.host + '/api/appointment/' + id, { headers: headers })
      .pipe(map((response: any) => response,
        (error: any) => {
          throw error;
        }));

  }

  getNextEvaluationsForFamilies(): Observable<FamilyEvaluationAppointment[]> {
    console.log('entering getNextEvaluationsForFamilies');
    const headers = State.getAuthHeaders();
    const params = new HttpParams()
      .append('providerId', '' + State.config.providerId);
    return this.http.get(State.config.host + '/api/family-evaluation-appointments',
      { headers: headers, params: params })
      .pipe(map((response: any) => response,
        (error: any) => {
          throw error;
        }));
  }


  getFamilyInterventionAppointments(): Observable<FamilyInterventionAppointment[]> {
    console.log('entering getFamilyInterventionAppointments');
    const headers = State.getAuthHeaders();
    const params = new HttpParams()
      .append('providerId', '' + State.config.providerId);
    return this.http.get(State.config.host + '/api/family-intervention-appointments',
      { headers: headers, params: params })
      .pipe(map((response: any) => response,
        (error: any) => {
          throw error;
        }));
  }

  incrementLoadCount() {
    this.loadCount++;
    if (this.loadCount >= 3) {
      State.isLoaded = true;
    }
  }

  saveAppointment(appointment: Appointment) {

    return this.http.post(State.config.host + '/api/appointment', appointment, { headers: State.getAuthHeaders().append('allowDupes', 'true') })
      .pipe(map((r: any) => r, (error: any) => {
        throw error;
      }));
  }

  reset() {
    this.loadCount = 0;
    State.isLoaded = false;
    this.getCalendarItems().subscribe(response => {
      State.setState({
        discriminator: 'calendarEvents',
        calendarEvents: response
      });
      this.incrementLoadCount();
      this.getSchedulableItems(-1, 10, 0, '', 'desc', 'is_late_flag').subscribe(response => {
        State.setState({
          discriminator: 'schedulableItems',
          schedulableItems: response
        });
        this.incrementLoadCount();
        this.getNextEvaluationsForFamilies().subscribe(response => {
          const familyEvaluationAppointments = response.map(item => {
            const tm = dayjs.unix(item.nextTargetDt);
            const now = dayjs();
            const daysBetween = now.diff(tm, 'day');
            if (item.status == 'ELIGIBLE') {
              item.lateness = 1000000;
            } else if (item.status == 'PENDING_ONBOARDING') {
              item.lateness = 0;
              const statusUpdate = item.statusUpdates.find(log => log.newValue == 'PENDING_ONBOARDING');
              if (statusUpdate) {
                const tm = dayjs(statusUpdate.changedAt);
                const daysBetween = now.diff(tm, 'day');
                item.lateness = (daysBetween <= 14) ? -2 : -3;
              }
            }
            else if (tm.isAfter(now) && daysBetween <= 14) {
              item.lateness = -2;
            } else if (tm.isBefore(now)) {
              if (daysBetween <= 7) {
                item.lateness = -3;
              } else if (daysBetween > 7 && daysBetween <= 21) {
                item.lateness = -4;
              } else if (daysBetween > 21 && daysBetween < 49) {
                item.lateness = -1;
              }
              else {
                item.lateness = daysBetween;
              }
            }
            return item;
          }).sort((a, b) => {
            if (a.lateness === undefined && b.lateness === undefined) return 0;
            if (a.lateness === undefined) return 1;  // undefined sorts last
            if (b.lateness === undefined) return -1;
            return a.lateness - b.lateness;
          });
          State.setState({
            discriminator: 'familyEvaluationAppointments',
            familyEvaluationAppointments: familyEvaluationAppointments
          });
        });
          this.getFamilyInterventionAppointments().subscribe(response => {
            const familyInterventionAppointments = response.map(item => {
              const tm = dayjs.unix(item.onboardingDate);
              const now = dayjs();
              const daysBetween = now.diff(tm, 'day');
              if (daysBetween <= 60) {
                item.lateness = 3;
              } else if (daysBetween > 60 && daysBetween <= 120) {
                item.lateness = 2;
              } else if (daysBetween > 120 && daysBetween <= 150) {
                item.lateness = 1;
              } else {
                item.lateness = 4;
              }
              if (item.nextAppointment) {
                item.nextAppointmentEpoch = item.nextAppointment.startDateTime;
              }
              return item;
            }).sort((a, b) => {
              if (a.lateness === undefined && b.lateness === undefined) return 0;
              if (a.lateness === undefined) return 1;  // undefined sorts last
              if (b.lateness === undefined) return -1;
              return a.lateness - b.lateness;
            });
            State.setState({
              discriminator: 'familyInterventionAppointments',
              familyInterventionAppointments: familyInterventionAppointments
            });
            this.incrementLoadCount();
          });
        });
      });


      if (State.currentFamily && State.currentFamily.id) {
        this.getFamilyAppointments(State.currentFamily.id).subscribe(response => {
          State.setState({
            discriminator: 'familyAppointments',
            familyAppointments: response
          });
        });
      }
    }
  }
